import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Cookie } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLegal } from '../lib/legalStrings';

const STORAGE_KEY = 'adforge_cookie_consent';

// Routes where the cookie banner must never render (e.g. static asset pages
// captured for marketing screenshots).
const HIDDEN_ROUTES = ['/end-card'];

export default function CookieBanner() {
  const { language } = useLanguage();
  const location = useLocation();
  const L = useLegal(language);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (HIDDEN_ROUTES.includes(location.pathname)) return;
    // Show if consent hasn't been recorded yet
    const existing = localStorage.getItem(STORAGE_KEY);
    if (!existing) {
      // Small delay so the banner doesn't fight the page entrance animation
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [location.pathname]);

  const record = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      choice,          // 'accept' | 'reject'
      timestamp: new Date().toISOString(),
      version: 1
    }));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-50"
          data-testid="cookie-banner"
        >
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-4 h-4 text-primary" aria-hidden="true" />
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-1">{L.cookies.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {L.cookies.body}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                size="sm"
                variant="outline"
                onClick={() => record('reject')}
                data-testid="cookie-reject"
                className="flex-1 text-xs"
              >
                {L.cookies.reject}
              </Button>
              <Button
                size="sm"
                onClick={() => record('accept')}
                data-testid="cookie-accept"
                className="flex-1 text-xs"
              >
                {L.cookies.accept}
              </Button>
            </div>
            <Link
              to="/cookies"
              className="block mt-3 text-[11px] text-muted-foreground hover:text-primary underline text-center"
              data-testid="cookie-banner-learn-more"
            >
              {L.cookies.learnMore}
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
